import { useCallback, useEffect, useRef, useState } from 'react';
import { CtxDeshacer, type Ofrecimiento } from './deshacer-contexto';
import { Undo2, X } from 'lucide-react';

/**
 * DESHACER — la red debajo de cada acción.
 *
 * Mandato: *"Cada acción muestra un aviso abajo con «Deshacer» durante 5
 * segundos."*
 *
 * ═══ POR QUÉ NO ES UN `toast` MÁS ═══
 *
 * Un aviso informa; esto propone revertir. La diferencia importa porque cambia
 * el costo de equivocarse: con Deshacer, deslizar sin mirar es barato, y
 * deslizar sin mirar es justo lo que el dueño va a hacer con la lista del día.
 * Sin esta red, cada gesto exige atención, y una bandeja que exige atención en
 * cada fila no se vacía nunca.
 *
 * ═══ SE APILA UNO SOLO ═══
 *
 * Si llega otra acción antes de que expire la anterior, la anterior se da por
 * confirmada y desaparece. Dos barras de deshacer superpuestas obligan a leer
 * cuál es cuál, que es más trabajo del que ahorran.
 */

const SEGUNDOS = 5;

export function DeshacerProvider({ children }: { children: React.ReactNode }) {
  const [actual, setActual] = useState<Ofrecimiento | null>(null);
  const [restante, setRestante] = useState(SEGUNDOS);
  const reloj = useRef<ReturnType<typeof setInterval> | null>(null);

  const limpiar = useCallback(() => {
    if (reloj.current) clearInterval(reloj.current);
    reloj.current = null;
    setActual(null);
  }, []);

  const ofrecerDeshacer = useCallback(
    (o: Ofrecimiento) => {
      if (reloj.current) clearInterval(reloj.current);
      setActual(o);
      setRestante(SEGUNDOS);
      reloj.current = setInterval(() => {
        setRestante((s) => {
          if (s <= 1) {
            if (reloj.current) clearInterval(reloj.current);
            reloj.current = null;
            setActual(null);
            return SEGUNDOS;
          }
          return s - 1;
        });
      }, 1000);
    },
    [],
  );

  useEffect(() => () => void (reloj.current && clearInterval(reloj.current)), []);

  return (
    <CtxDeshacer.Provider value={{ ofrecerDeshacer }}>
      {children}
      {actual && (
        <div
          role="status"
          className="fixed inset-x-4 bottom-[88px] z-40 mx-auto flex max-w-md items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-sm text-white shadow-lg md:bottom-6 dark:bg-slate-800"
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <span className="min-w-0 flex-1 truncate">{actual.texto}</span>
          <button
            onClick={() => {
              void actual.deshacer();
              limpiar();
            }}
            className="flex min-h-[36px] shrink-0 items-center gap-1 rounded-lg bg-white/15 px-2.5 font-medium"
          >
            <Undo2 className="h-4 w-4" /> Deshacer
            <span className="tabular-nums text-white/60">{restante}</span>
          </button>
          <button
            onClick={limpiar}
            aria-label="Cerrar"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-white/60"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </CtxDeshacer.Provider>
  );
}
